import React, { useState } from "react";
import {
  Card,
  CardBody,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";
import toast from "react-hot-toast";
import { Link, useNavigate } from "react-router-dom";
import RecipeService from "../../../services/OrderingServices/RecipeService";
import DeleteModal from "../../../Components/Common/DeleteModal";
import ReceipeForm from "../component/RecipeForm";
import ResponsiveDataManagement from "../../../Components/Common/ResponsiveManagementLayout";

import useServerSideSearch from "../../../hooks/useServerSideSearch";

const RecipeList = () => {
  const navigate = useNavigate();

  const {
    data: recipeList,
    loading,
    totalRows,
    currentPage,
    pageSize,
    searchQuery,
    handleSearch,
    handlePaginationChange,
    refetch,
  } = useServerSideSearch(
    RecipeService.getList,
    [], // searchKeys
    500, // debounceDelay
    (response) => ({
      data: Array.isArray(response) ? response : [],
      totalCount: Array.isArray(response) ? response.length : 0,
      stats: {},
    })
  );

  // Modal states
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const [addModal, setAddModal] = useState(false);
  const [openDropdownId, setOpenDropdownId] = useState(null);

  const columns = [
    {
      name: "S.N",
      selector: (row, index) => (currentPage - 1) * pageSize + index + 1,
      sortable: false,
      width: "75px",
    },
    {
      name: "Name",
      selector: (row) => row.productName,
      cell: (row) => (
        <Link to={`/receipe/detail/${row.id}`}>{row.productName}</Link>
      ),
      sortable: true,
      width: "200px",
      wrap: true,
    },
    {
      name: "Description",
      selector: (row) => row.description || "N/A",
      sortable: true,
      wrap: true,
    },
  ];

  const handleDelete = async () => {
    if (selectedRecipe) {
      try {
        await RecipeService.delete(selectedRecipe);
        toast.success("Recipe deleted successfully");
        refetch(); // ← Use refetch to reload data
        setDeleteModal(false);
        setSelectedRecipe(null);
      } catch (error) {
        console.error("Error deleting recipe:", error);
        toast.error(
          error?.response?.data?.message || "Failed to delete recipe"
        );
      }
    }
  };

  // Handle modal for add/edit
  const handleAdd = () => {
    setSelectedRecipe(null);
    setAddModal(true);
  };

  const handleEdit = (recipe) => {
    setSelectedRecipe(recipe.id);
    setAddModal(true);
  };

  const handleView = (recipe) => {
    navigate(`/receipe/detail/${recipe.id}`);
  };

  const handleDeleteClick = (recipe) => {
    setSelectedRecipe(recipe.id);
    setDeleteModal(true);
  };

  return (
    <>
      <ResponsiveDataManagement
        title="Receipe Management"
        pageTitle="Inventory Management"
        mobileCardTitle="Receipe Lists"
        data={recipeList}
        columns={columns}
        searchKeys={["productName", "description"]}
        onAdd={handleAdd}
        onEdit={handleEdit}
        // onView={handleView}
        onDelete={handleDeleteClick}
        loading={loading}
        emptyStateMessage="No recipes found"
        totalRows={totalRows}
        currentPage={currentPage}
        pageSize={pageSize}
        onSearch={handleSearch}
        searchTerm={searchQuery}
        onPaginationChange={handlePaginationChange}
        showStats={false} // No stats for recipes
        mobileCardRenderer={(item) => (
          <Card
            className="shadow-sm rounded-3 border-0 border-start border-4 border-primary mb-3"
            onClick={() => handleView(item)}
          >
            <CardBody>
              <div className="d-flex justify-content-between align-items-start">
                <div className="flex-grow-1">
                  <h6 className="fw-bold text-primary mb-2">
                    {item.productName}
                  </h6>
                  <p className="mb-1 text-muted small">
                    <strong>Description:</strong> {item.description || "N/A"}
                  </p>
                </div>

                <Dropdown
                  isOpen={openDropdownId === item.id}
                  toggle={() =>
                    setOpenDropdownId(
                      openDropdownId === item.id ? null : item.id
                    )
                  }
                  direction="down"
                >
                  <DropdownToggle
                    color="light"
                    className="btn-sm rounded-circle shadow-sm"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <i className="ri-more-2-fill" />
                  </DropdownToggle>

                  <DropdownMenu end>
                    <DropdownItem
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEdit(item);
                        setOpenDropdownId(null);
                      }}
                    >
                      <i className="ri-edit-line me-2" />
                      Edit
                    </DropdownItem>

                    <DropdownItem
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteClick(item);
                        setOpenDropdownId(null);
                      }}
                    >
                      <i className="ri-delete-bin-line me-2" />
                      Delete
                    </DropdownItem>
                  </DropdownMenu>
                </Dropdown>
              </div>
            </CardBody>
          </Card>
        )}
      />

      {/* Modals */}
      <ReceipeForm
        modal={addModal}
        toggleModal={() => {
          setAddModal(false);
          setSelectedRecipe(null);
        }}
        selectedRecipe={selectedRecipe}
        fetchRecipes={refetch}
      />

      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDelete}
        onCloseClick={() => {
          setDeleteModal(false);
          setSelectedRecipe(null);
        }}
        selectedItem={selectedRecipe}
      />
    </>
  );
};

export default RecipeList;
